/**
 * mobile_monthly_first_v1 A/B test (started 2026-11-02): on a phone, does an
 * upgrade modal that opens on the Monthly plans instead of the Yearly ones
 * turn more paywall visitors into buyers?
 *
 * Nov 1 2026 read (PostHog, Oct 2–31, first checkout per person, `purchase`
 * within 2 h, rich countries): plan checkouts started from this modal closed
 * 49% on desktop and 12% on mobile, and 75% of the mobile starters were back
 * on the site unpaid within 30 minutes: they saw Stripe's price and left.
 * The modal opens on the Yearly tab for every device; the card says
 * "$29 /mo" and Stripe then asks for $348. Mobile Plus yearly 173 clicks →
 * 19 paid (11%), Plus monthly 101 → 20 (20%), Creator yearly 51 → 1. On
 * desktop yearly and monthly close alike (49% vs 53%), so the Yearly default
 * costs nothing there.
 *
 * "control" opens the modal on the tab the caller asked for, as today.
 * "monthly" opens a modal that would open on Yearly on the Monthly plans
 * instead; the Yearly tab stays one tap away, and the cards, the highlight
 * and the packs are unchanged.
 *
 * Who is in the test: a visitor on a phone-width viewport (under Tailwind's
 * md breakpoint, where the cards stack), when the modal would open on the
 * Yearly tab and the account has no active paid plan. Never drawn: the tool
 * pages' pop-up (creative_tool_* triggers belong to tool_lite_offer_v1), the
 * resume-checkout banner (the visitor already picked a plan there), and a
 * modal growth_packs_first_v1 moved to the packs (it asks for "topup" by
 * then, so it is not a Yearly opening).
 *
 * The browser is the unit: drawn on the first eligible opening and kept in
 * localStorage. A signed-in account draws from a salted FNV-1a bucket of its
 * user id, so it gets the same arm on every device it signs in on.
 *
 * Kill switch: VITE_MOBILE_MONTHLY_FIRST_PERCENT=0 (build-time, default 50)
 * opens every modal as the caller asked, draws nothing and stamps nothing.
 * Other values only decide new draws; stored arms stay.
 *
 * Readout: `mobile_monthly_first_exposure` (experiment, arm, trigger,
 * requested_tab, signed_in) on every eligible opening in either arm, the
 * same arm as `mobile_monthly_first_arm` on upgrade_modal_shown,
 * upgrade_cta_clicked, checkout_started_from_modal and topup_pack_clicked,
 * and the person property mobile_monthly_first_v1_arm (set once on the
 * exposure, and again at sign-up/in through useAuth, since PostHog drops
 * $set_once for anonymous visitors). Then per exposed person by arm:
 * checkout starts, `purchase` within 2 h (the primary: buyers per exposed
 * visitor) and first-order value (the guardrail: a yearly order is 10x a
 * monthly one, so fewer yearly sales must be paid for in buyers). About 90
 * rich-country mobile plan checkouts a week start from this modal, so read
 * after two weeks.
 */

import { fnv1a32 } from "./directorStyleBoost";

export const MOBILE_MONTHLY_FIRST_EXPERIMENT = "mobile_monthly_first_v1";

export type MobileMonthlyFirstArm = "control" | "monthly";

/** The modal tabs (UpgradeModal's UpgradeModalTab). */
type ModalTab = "monthly" | "yearly" | "topup";

export const MOBILE_MONTHLY_FIRST_STORAGE_KEY = `solmi:exp:${MOBILE_MONTHLY_FIRST_EXPERIMENT}`;

/** The person property carrying the arm. */
export const MOBILE_MONTHLY_FIRST_PERSON_PROPERTY = `${MOBILE_MONTHLY_FIRST_EXPERIMENT}_arm`;

/**
 * The widest phone viewport: one pixel under Tailwind's `md` breakpoint,
 * where the modal stops stacking its plan cards (the same line as
 * useIsMobile in hooks/useMobile.tsx).
 */
export const PHONE_VIEWPORT_MAX_WIDTH = 767;

function windowWidth(): number | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.innerWidth;
  } catch {
    return undefined;
  }
}

/**
 * Whether a viewport this wide gets the stacked phone layout. `width`
 * defaults to the window's; an unknown or zero width is not a phone.
 */
export function isPhoneViewport(
  width: number | undefined = windowWidth(),
): boolean {
  return (
    typeof width === "number" &&
    Number.isFinite(width) &&
    width > 0 &&
    width <= PHONE_VIEWPORT_MAX_WIDTH
  );
}

export function mobileMonthlyFirstPercent(raw: unknown): number {
  if (raw === undefined || raw === null || raw === "") return 50;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 50;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function mobileMonthlyFirstBucket(seed: string): number {
  return fnv1a32(`${MOBILE_MONTHLY_FIRST_EXPERIMENT}:${seed}`) % 100;
}

/** Triggers that never draw: their pop-up or plan belongs elsewhere. */
export function mobileMonthlyFirstExcludedTrigger(trigger: string): boolean {
  return (
    trigger.startsWith("creative_tool_") || trigger === "resume_checkout_banner"
  );
}

/**
 * Whether this opening may draw (or use) an arm: a phone-width viewport, a
 * modal about to open on the Yearly tab, no active paid plan, and a trigger
 * that isn't excluded.
 */
export function mobileMonthlyFirstEligible(input: {
  phone: boolean;
  requestedTab: ModalTab;
  trigger: string;
  hasActivePaidPlan: boolean;
}): boolean {
  return (
    input.phone &&
    input.requestedTab === "yearly" &&
    !input.hasActivePaidPlan &&
    !mobileMonthlyFirstExcludedTrigger(input.trigger)
  );
}

function isArm(value: unknown): value is MobileMonthlyFirstArm {
  return value === "control" || value === "monthly";
}

type ArmStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): ArmStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Reading window.localStorage itself throws when site data is blocked.
    return null;
  }
}

function readStoredArm(
  storage: ArmStorage | null,
): MobileMonthlyFirstArm | null {
  try {
    const stored = storage?.getItem(MOBILE_MONTHLY_FIRST_STORAGE_KEY);
    return isArm(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * This browser's arm, or null when the test is off. Reads the stored arm
 * first; otherwise draws one (from the user id when given, else at random)
 * and stores it. Storage that throws still yields an arm, just not a sticky
 * one. `storage` defaults to localStorage.
 */
export function mobileMonthlyFirstArm(input: {
  userId?: string | number | null;
  percent: number;
  storage?: ArmStorage | null;
  random?: () => number;
}): MobileMonthlyFirstArm | null {
  if (input.percent <= 0) return null;
  const storage =
    input.storage === undefined ? browserStorage() : input.storage;
  const stored = readStoredArm(storage);
  if (stored) return stored;
  const userId =
    input.userId === null || input.userId === undefined
      ? ""
      : String(input.userId);
  const bucket = userId
    ? mobileMonthlyFirstBucket(userId)
    : Math.floor((input.random ?? Math.random)() * 100);
  const arm: MobileMonthlyFirstArm =
    bucket < input.percent ? "monthly" : "control";
  try {
    storage?.setItem(MOBILE_MONTHLY_FIRST_STORAGE_KEY, arm);
  } catch {}
  return arm;
}

/**
 * The tab the modal opens on: the Monthly plans instead of the Yearly ones
 * in the "monthly" arm. Any other requested tab opens as asked.
 */
export function mobileMonthlyFirstTab(
  requestedTab: ModalTab,
  arm: MobileMonthlyFirstArm | null,
): ModalTab {
  return arm === "monthly" && requestedTab === "yearly"
    ? "monthly"
    : requestedTab;
}

/** What mobile_monthly_first_exposure carries (plus trigger and tab). */
export function mobileMonthlyFirstEventProps(
  arm: MobileMonthlyFirstArm | null,
): Record<string, string> {
  return arm ? { experiment: MOBILE_MONTHLY_FIRST_EXPERIMENT, arm } : {};
}

/**
 * The same arm on upgrade_modal_shown and the modal's click events, under a
 * key of its own: those events' `experiment` and `arm` belong to
 * shortfall_ask_v1.
 */
export function mobileMonthlyFirstModalProps(
  arm: MobileMonthlyFirstArm | null,
): { mobile_monthly_first_arm?: MobileMonthlyFirstArm } {
  return arm ? { mobile_monthly_first_arm: arm } : {};
}

/** The person property to set once, or undefined outside the test. */
export function mobileMonthlyFirstPersonProps(
  arm: MobileMonthlyFirstArm | null,
): Record<string, string> | undefined {
  return arm ? { [MOBILE_MONTHLY_FIRST_PERSON_PROPERTY]: arm } : undefined;
}

/**
 * The person property to set once when a visitor signs up or in (useAuth's
 * identify call): the arm this browser already holds, undefined if none or
 * with the test off. It never draws.
 */
export function mobileMonthlyFirstIdentifyProps(
  rawPercent: unknown,
  storage?: ArmStorage | null,
): Record<string, string> | undefined {
  if (mobileMonthlyFirstPercent(rawPercent) <= 0) return undefined;
  return mobileMonthlyFirstPersonProps(
    readStoredArm(storage === undefined ? browserStorage() : storage),
  );
}
